// components/AnimatedValueCardMD.tsx
import { useEffect, useState } from 'react';
import { Card } from '../../../components/Card';
import styles from '../style.module.css';

interface AnimatedValueCardMDProps {
  title: string;
  value: number | string;
  icon?: string;
  showTutorial?: boolean;
  onTutorialClick?: () => void;
  tutorialIcon?: string;
  tutorialText?: string;
  isTextOnly?: boolean; // Nova prop para valores não numéricos
  additionalText?: string; // Texto adicional (como "vendas")
}

export function ProgramCard({
  title,
  value,
  icon,
  showTutorial = false,
  onTutorialClick,
  tutorialIcon,
  tutorialText,
  isTextOnly = false,
  additionalText,
}: AnimatedValueCardMDProps) {
  const [displayValue, setDisplayValue] = useState(isTextOnly ? '' : '0');

  useEffect(() => {
    if (isTextOnly) {
      // Para valores textuais, apenas define o valor final
      setDisplayValue(value.toString());
      return;
    }

    // Para valores numéricos, anima do zero ao valor final
    const numericValue =
      typeof value === 'number' ? value : parseFloat(value as string);

    if (isNaN(numericValue)) {
      setDisplayValue(value as string);
      return;
    }

    const duration = 1500; // 1.5 segundos
    const startTime = Date.now();
    const startValue = 0;
    const endValue = numericValue;

    const animate = () => {
      const now = Date.now();
      const progress = Math.min((now - startTime) / duration, 1);
      const currentValue = startValue + progress * (endValue - startValue);

      // Formata o valor para exibição (sem decimais)
      setDisplayValue(Math.floor(currentValue).toLocaleString());

      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        setDisplayValue(endValue.toLocaleString());
      }
    };

    requestAnimationFrame(animate);
  }, [value, isTextOnly]);

  return (
    <Card>
      <div className="d-flex justify-content-between">
        <span className="w-100">
          <p className="fw-semibold m-0">{title}</p>
          <p
            className={`fw-semibold color-text-primary fs-4 m-0 ${additionalText && additionalText !== 'vendas' && 'text-center'}`}
          >
            {displayValue}
            {additionalText && additionalText !== 'vendas' && <br />}
            {additionalText && value && (
              <span className="ms-2">{additionalText}</span>
            )}
          </p>
        </span>
        {icon && (
          <div className={styles.iconCard}>
            <i className="material-icons-outlined color-text">{icon}</i>
          </div>
        )}
      </div>
      {showTutorial && (
        <>
          <hr />
          <div
            className="d-flex justify-content-between clickable"
            onClick={onTutorialClick}
          >
            <span className="d-flex align-items-center">
              {tutorialIcon && (
                <i className={`material-icons-outlined me-3 fs-4 text-warning`}>
                  {tutorialIcon}
                </i>
              )}
              {tutorialText && (
                <span className="fw-semibold">{tutorialText}</span>
              )}
            </span>
            <i className="material-icons-outlined color-text">
              {tutorialText === 'Detalhes' ? 'chevron_right' : 'play_arrow'}
            </i>
          </div>
        </>
      )}
    </Card>
  );
}
